// Bilingual (EN / HI) 9-month pregnancy diet plan.
// Rendered via useLang().tr() in <MealPlan /> when goal === 'pregnancy'.
// One universal plan per month (no veg/non-veg toggle per user's choice).

export const pregnancyPlan = [
  {
    month: 1,
    weeks: { en: 'Weeks 1–4', hi: 'सप्ताह 1–4' },
    focus: { en: 'Folic acid and early fetal development', hi: 'फोलिक एसिड व शुरुआती भ्रूण विकास' },
    schedule: [
      { time: { en: 'Breakfast', hi: 'नाश्ता' }, food: { en: 'Vegetable poha/oats + milk', hi: 'वेज पोहा/ओट्स + दूध' } },
      { time: { en: 'Mid-morning snack', hi: 'दिन-पूर्व नाश्ता' }, food: { en: 'Apple or orange', hi: 'सेब या संतरा' } },
      { time: { en: 'Lunch', hi: 'दोपहर का भोजन' }, food: { en: '2 rotis + dal + green vegetables + curd', hi: '2 रोटी + दाल + हरी सब्ज़ी + दही' } },
      { time: { en: 'Evening snack', hi: 'शाम का नाश्ता' }, food: { en: 'Roasted chana + coconut water', hi: 'भुना चना + नारियल पानी' } },
      { time: { en: 'Dinner', hi: 'रात का भोजन' }, food: { en: 'Rice + dal + vegetables + paneer/chicken', hi: 'चावल + दाल + सब्ज़ी + पनीर/चिकन' } },
      { time: { en: 'Before bed', hi: 'सोने से पहले' }, food: { en: 'Warm milk', hi: 'गर्म दूध' } },
    ],
    include: { en: 'Include: Folic acid, leafy greens, sprouts, citrus fruits.', hi: 'शामिल करें: फोलिक एसिड, हरी पत्तेदार सब्ज़ियाँ, स्प्राउट्स, खट्टे फल।' },
  },
  {
    month: 2,
    weeks: { en: 'Weeks 5–8', hi: 'सप्ताह 5–8' },
    focus: { en: 'Reduce nausea, maintain hydration', hi: 'मिचली कम करें, हाइड्रेशन बनाए रखें' },
    tips: [
      { en: 'Small meals every 2–3 hours', hi: 'हर 2–3 घंटे में छोटे-छोटे भोजन' },
      { en: 'Dry crackers or toast before getting out of bed if nauseous', hi: 'मिचली हो तो बिस्तर से उठने से पहले सूखे क्रैकर या टोस्ट' },
      { en: 'Banana, curd, khichdi, soups', hi: 'केला, दही, खिचड़ी, सूप' },
      { en: 'Ginger tea (if approved by your doctor)', hi: 'अदरक की चाय (डॉक्टर की सलाह से)' },
    ],
    include: { en: 'Include: Bananas, yogurt, rice, lentils, eggs.', hi: 'शामिल करें: केला, दही, चावल, दाल, अंडे।' },
  },
  {
    month: 3,
    weeks: { en: 'Weeks 9–12', hi: 'सप्ताह 9–12' },
    focus: { en: 'Protein and iron', hi: 'प्रोटीन व आयरन' },
    tips: [
      { en: 'Eggs / paneer', hi: 'अंडे / पनीर' },
      { en: 'Dal and beans', hi: 'दाल व बीन्स' },
      { en: 'Green leafy vegetables', hi: 'हरी पत्तेदार सब्ज़ियाँ' },
      { en: 'Dates and raisins', hi: 'खजूर व किशमिश' },
      { en: 'Vitamin C-rich fruits to improve iron absorption', hi: 'आयरन अवशोषण के लिए विटामिन C वाले फल' },
    ],
  },
  {
    month: 4,
    weeks: { en: 'Weeks 13–16', hi: 'सप्ताह 13–16' },
    focus: { en: 'Calcium and protein', hi: 'कैल्शियम व प्रोटीन' },
    tips: [
      { en: 'Milk (2 glasses/day)', hi: 'दूध (2 गिलास/दिन)' },
      { en: 'Curd', hi: 'दही' },
      { en: 'Paneer', hi: 'पनीर' },
      { en: 'Sesame seeds', hi: 'तिल के बीज' },
      { en: 'Almonds', hi: 'बादाम' },
      { en: 'Chicken / fish (well cooked) if non-vegetarian', hi: 'मांसाहारी हैं तो अच्छे से पका चिकन / मछली' },
    ],
  },
  {
    month: 5,
    weeks: { en: 'Weeks 17–20', hi: 'सप्ताह 17–20' },
    focus: { en: 'Healthy weight gain', hi: 'स्वस्थ वज़न बढ़ोतरी' },
    tips: [
      { en: 'Whole grains', hi: 'साबुत अनाज' },
      { en: 'Fruits', hi: 'फल' },
      { en: 'Vegetables', hi: 'सब्ज़ियाँ' },
      { en: 'Healthy snacks like nuts and seeds', hi: 'नट्स व बीज जैसे स्वस्थ स्नैक्स' },
      { en: 'Plenty of water', hi: 'भरपूर पानी' },
    ],
  },
  {
    month: 6,
    weeks: { en: 'Weeks 21–24', hi: 'सप्ताह 21–24' },
    focus: { en: 'Iron and fiber', hi: 'आयरन व फ़ाइबर' },
    tips: [
      { en: 'Spinach', hi: 'पालक' },
      { en: 'Beetroot', hi: 'चुकंदर' },
      { en: 'Lentils', hi: 'दालें' },
      { en: 'Whole grains', hi: 'साबुत अनाज' },
      { en: 'Prunes / guava for constipation', hi: 'कब्ज के लिए आलूबुखारा / अमरूद' },
      { en: 'Buttermilk', hi: 'छाछ' },
    ],
  },
  {
    month: 7,
    weeks: { en: 'Weeks 25–28', hi: 'सप्ताह 25–28' },
    focus: { en: "Baby's brain development", hi: 'शिशु के मस्तिष्क विकास' },
    tips: [
      { en: 'Walnuts', hi: 'अखरोट' },
      { en: 'Flaxseeds', hi: 'अलसी के बीज' },
      { en: 'Low-mercury fish (well cooked) or vegetarian omega-3 sources', hi: 'कम-पारा मछली (अच्छे से पकी) या शाकाहारी ओमेगा-3 स्रोत' },
      { en: 'Eggs', hi: 'अंडे' },
      { en: 'Milk', hi: 'दूध' },
    ],
  },
  {
    month: 8,
    weeks: { en: 'Weeks 29–32', hi: 'सप्ताह 29–32' },
    focus: { en: 'Energy and digestion', hi: 'ऊर्जा व पाचन' },
    tips: [
      { en: 'Small frequent meals', hi: 'छोटे व बार-बार भोजन' },
      { en: 'Vegetable soups', hi: 'वेज सूप' },
      { en: 'Fresh fruits', hi: 'ताज़े फल' },
      { en: 'Protein-rich meals', hi: 'प्रोटीन युक्त भोजन' },
      { en: 'Avoid heavy, oily foods', hi: 'भारी व तैलीय भोजन से बचें' },
    ],
  },
  {
    month: 9,
    weeks: { en: 'Weeks 33–40', hi: 'सप्ताह 33–40' },
    focus: { en: 'Prepare for delivery', hi: 'डिलीवरी की तैयारी' },
    tips: [
      { en: 'Easily digestible meals', hi: 'आसानी से पचने वाला भोजन' },
      { en: 'Plenty of fluids', hi: 'भरपूर तरल पदार्थ' },
      { en: 'Fresh fruits', hi: 'ताज़े फल' },
      { en: 'Vegetables', hi: 'सब्ज़ियाँ' },
      { en: 'Protein with every meal', hi: 'हर भोजन में प्रोटीन' },
      { en: 'Continue calcium and iron as prescribed', hi: 'डॉक्टर के अनुसार कैल्शियम व आयरन जारी रखें' },
    ],
  },
];

export const pregnancyDailyFoods = {
  title: { en: 'Daily foods throughout pregnancy', hi: 'गर्भावस्था में रोज़ाना खाने योग्य भोजन' },
  items: [
    { label: { en: 'Milk / Curd', hi: 'दूध / दही' }, value: { en: '2–3 servings', hi: '2–3 सर्विंग' } },
    { label: { en: 'Protein', hi: 'प्रोटीन' }, value: { en: 'Eggs, paneer, dal, chicken, fish (well cooked), soy', hi: 'अंडे, पनीर, दाल, चिकन, अच्छे से पकी मछली, सोया' } },
    { label: { en: 'Fruits', hi: 'फल' }, value: { en: '2–3 servings', hi: '2–3 सर्विंग' } },
    { label: { en: 'Vegetables', hi: 'सब्ज़ियाँ' }, value: { en: '3–5 servings', hi: '3–5 सर्विंग' } },
    { label: { en: 'Whole grains', hi: 'साबुत अनाज' }, value: { en: 'Roti, rice, oats, millet', hi: 'रोटी, चावल, ओट्स, बाजरा/ज्वार' } },
    { label: { en: 'Nuts', hi: 'नट्स' }, value: { en: 'Almonds, walnuts', hi: 'बादाम, अखरोट' } },
    { label: { en: 'Water', hi: 'पानी' }, value: { en: '2.5–3 litres daily', hi: '2.5–3 लीटर रोज़' } },
  ],
};

export const pregnancyAvoid = {
  title: { en: 'Foods to avoid', hi: 'बचने योग्य भोजन' },
  items: [
    { en: 'Alcohol and smoking', hi: 'शराब व धूम्रपान' },
    { en: 'Raw or undercooked meat, fish, or eggs', hi: 'कच्चा या अधपका मांस, मछली या अंडे' },
    { en: 'Unpasteurized dairy products', hi: 'बिना पाश्चुरीकृत डेयरी उत्पाद' },
    { en: 'High-mercury fish', hi: 'अधिक पारा वाली मछली' },
    { en: 'Excess caffeine (limit to about 200 mg/day)', hi: 'अधिक कैफ़ीन (लगभग 200 mg/दिन तक सीमित)' },
    { en: 'Unwashed fruits and vegetables', hi: 'बिना धुले फल व सब्ज़ियाँ' },
  ],
};

export const pregnancyDisclaimer = {
  en: 'This is a general plan suitable for most healthy pregnancies. Regular prenatal check-ups and following your obstetrician\u2019s advice on supplements (such as folic acid, iron, calcium, and vitamin D) are essential. Always consult your doctor before making changes to your diet during pregnancy.',
  hi: 'यह एक सामान्य प्लान है जो अधिकांश स्वस्थ गर्भावस्थाओं के लिए उपयुक्त है। नियमित प्रसवपूर्व जाँच और अपने प्रसूति विशेषज्ञ की सलाह से सप्लीमेंट (जैसे फोलिक एसिड, आयरन, कैल्शियम व विटामिन D) लेना ज़रूरी है। गर्भावस्था में डाइट बदलने से पहले हमेशा अपने डॉक्टर से परामर्श लें।',
};
